import React, { useState, useMemo } from 'react';
import FileXMLUploadComponent from '../utils/FileXMLUploadComponent';

const XMLFileProcessor = () => {
  const [parsedFiles, setParsedFiles] = useState([]);
  const [resetKey, setResetKey] = useState(0); // Add this line
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const handleFilesParsed = (parsedFilesWithContent) => {
    const newFiles = parsedFilesWithContent.map(fileWithContent => ({
      file: fileWithContent.file,
      content: fileWithContent.content // Assuming this is already the processed content
    }));
    console.log(parsedFilesWithContent);
    setParsedFiles(current => [...current, ...newFiles]);
  };

  // Add a method to clear all data
  const handleClearAll = () => {
    setParsedFiles([]);
    setResetKey(prevKey => prevKey + 1);
  };


  const downloadCSV = (array) => {
    const csvRows = [];
    // Get headers
    const headers = Object.keys(array[0]);
    csvRows.push(headers.join(','));

    // Loop over the rows
    for (const row of array) {
      const values = headers.map(header => {
        const escaped = ('' + row[header]).replace(/"/g, '\\"');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(','));
    }

    // Form the CSV string
    const csvString = csvRows.join('\n');

    // Create a Blob with CSV data
    const blob = new Blob([csvString], { type: 'text/csv' });

    // Create an anchor element and trigger the download
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'grid-data.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };


  const extractRequiredDataForTable = (parsedFiles) => {
    return parsedFiles.map(file => {

      if (!file || !file.content || !file.content['cfdi:Comprobante']) {
        // Handle the case where the data structure is not as expected
        console.error('Unexpected file content structure:', file);
        return null; // or return an empty object, or however you want to handle this case
      }

      const comp = file.content['cfdi:Comprobante'];

      // Handle 'cfdi:Emisor' and 'cfdi:Receptor' as arrays
      const emisor = comp['cfdi:Emisor'] ? comp['cfdi:Emisor'][0] : {};
      const receptor = comp['cfdi:Receptor'] ? comp['cfdi:Receptor'][0] : {};

      // Cast 'Fecha' to a date type
      const fecha = comp['Fecha'] ? new Date(comp['Fecha']) : null;

      // Construct and return an object for table display
      return {
        fileName: file.file.name,
        Serie: comp['Serie'],
        Folio: comp['Folio'],
        Fecha: fecha ? fecha.toLocaleString() : 'N/A', // Format date or return 'N/A'
        FormaPago: comp['FormaPago'],
        CondicionesDePago: comp['CondicionesDePago'],
        SubTotal: comp['SubTotal'],
        Moneda: comp['Moneda'],
        Total: comp['Total'],
        TipoDeComprobante: comp['TipoDeComprobante'],
        Exportacion: comp['Exportacion'],
        MetodoPago: comp['MetodoPago'],
        Emisor_Rfc: emisor['Rfc'] || 'N/A',
        Emisor_Nombre: emisor['Nombre'] || 'N/A',
        Emisor_RegimenFiscal: emisor['RegimenFiscal'] || 'N/A',
        Receptor_Rfc: receptor['Rfc'] || 'N/A',
        Receptor_Nombre: receptor['Nombre'] || 'N/A',
        Receptor_UsoCFDI: receptor['UsoCFDI'] || 'N/A',
        Receptor_RegimenFiscalReceptor: receptor['RegimenFiscalReceptor'] || 'N/A',
        Receptor_DomicilioFiscalReceptor: receptor['DomicilioFiscalReceptor'] || 'N/A',
      };
    })
      .filter(Boolean);;
  };

  // the headers array to include the new fields
  const headers = [
    'fileName', 'Serie', 'Folio', 'Fecha', 'Emisor_Rfc', 'Emisor_Nombre',
    'Emisor_RegimenFiscal', 'Receptor_Rfc', 'Receptor_Nombre', 'Receptor_UsoCFDI',
    'Receptor_RegimenFiscalReceptor', 'Receptor_DomicilioFiscalReceptor', 'FormaPago', 'CondicionesDePago', 'SubTotal', 'Moneda', 'Total',
    'TipoDeComprobante', 'Exportacion', 'MetodoPago',
  ];


  // Data prepared for table display
  const tableData = extractRequiredDataForTable(parsedFiles);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedTableData = useMemo(() => {
    let sortableItems = [...tableData];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [tableData, sortConfig]);

  // Step 1: Generate unique RECEPTOR RFC values
  const uniqueRfcSet = new Set(tableData.map(item => item.Receptor_Rfc));

  // Step 2: Map each RECEPTOR RFC to a specific color
  const rfcColorMap = {};
  const colors = ['bg-red-100', 'bg-green-100', 'bg-blue-100', 'bg-yellow-100', 'bg-purple-100']; // Example color classes
  let colorIndex = 0;
  uniqueRfcSet.forEach(rfc => {
    rfcColorMap[rfc] = colors[colorIndex % colors.length]; // Cycle through colors if there are more RFCs than colors
    colorIndex++;
  });


  return (
    <div className="p-6">
      <FileXMLUploadComponent key={resetKey} onFilesParsed={handleFilesParsed} onClearAll={handleClearAll} />
      {tableData.length > 0 && (
        <>
          <button
            onClick={() => downloadCSV(tableData)}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Export as CSV
          </button>
          <div className="mt-6 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {headers.map(header => (
                    <th key={header} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort(header)}>
                      {header.replace('_', ' ')}
                      {/* Optionally, add an icon to show the sort direction */}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedTableData.map((row, index) => (
                  <tr key={index} className={rfcColorMap[row.Receptor_Rfc] || ''}>
                    {headers.map(header => (
                      <td key={header} className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {row[header] || 'N/A'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default XMLFileProcessor;